import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowRight } from "lucide-react";

/* Overline / eyebrow label */
export const Overline = ({ children, className = "", light = false }) => (
  <span
    className={`inline-block font-sans uppercase text-[0.7rem] font-medium tracking-[0.32em] ${
      light ? "text-white/60" : "text-terracotta"
    } ${className}`}
  >
    {children}
  </span>
);

/* Scroll-triggered reveal wrapper */
export const Reveal = ({ children, delay = 0, y = 34, className = "", ...rest }) => {
  const reduce = useReducedMotion();
  return (
    <motion.div
      initial={reduce ? { opacity: 0 } : { opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-12% 0px" }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
      {...rest}
    >
      {children}
    </motion.div>
  );
};

/* Masked line-by-line reveal for large display headings */
export const MaskLines = ({ lines, className = "", lineClassName = "", stagger = 0.12, delay = 0.15 }) => {
  const reduce = useReducedMotion();
  return (
    <span className={className}>
      {lines.map((line, i) => (
        <span key={i} className="block overflow-hidden">
          <motion.span
            className={`block ${lineClassName}`}
            initial={reduce ? { opacity: 0 } : { y: "110%" }}
            animate={reduce ? { opacity: 1 } : { y: "0%" }}
            transition={{ duration: 1, delay: delay + i * stagger, ease: [0.22, 1, 0.36, 1] }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </span>
  );
};

/* Primary CTA button */
export const CTA = ({ children, href = "#join", variant = "solid", className = "", ...rest }) => {
  const base =
    "group inline-flex items-center gap-3 font-sans text-sm font-medium tracking-[0.14em] uppercase px-8 py-4 transition-[transform,background-color,color,border-color] duration-300";
  const styles =
    variant === "solid"
      ? "bg-terracotta text-white hover:bg-ink hover:-translate-y-0.5"
      : "border border-ink/25 text-ink hover:border-terracotta hover:text-terracotta hover:-translate-y-0.5";
  const external = /^https?:\/\//.test(href);
  const extProps = external ? { target: "_blank", rel: "noopener noreferrer" } : {};
  return (
    <a href={href} className={`${base} ${styles} ${className}`} {...extProps} {...rest}>
      <span>{children}</span>
      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" strokeWidth={1.6} />
    </a>
  );
};

/* Magical-realism image: real photo with soft glow behind + parallax drift */
export const MagicImage = ({ src, alt, className = "", imgClassName = "", glow = "terracotta", speed = 60, testid }) => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [speed, -speed]);
  const glowColor = glow === "sage" ? "bg-sage/30" : "bg-terracotta/25";
  return (
    <div ref={ref} className={`relative ${className}`}>
      <div className={`glow-float absolute -inset-6 -z-10 rounded-full ${glowColor} blur-3xl`} />
      <motion.img
        src={src}
        alt={alt}
        data-testid={testid}
        loading="lazy"
        style={reduce ? undefined : { y }}
        className={`h-full w-full object-cover ${imgClassName}`}
      />
    </div>
  );
};
